import Bugsnag from "@bugsnag/js";
import { useRouter } from "next/router";
import qs, { ParsedQs } from "qs";

import { useFindByKey } from "utils/schema-utils";

import styles from "./AvatarPreview.module.scss";

type AvatarPreviewProps = {
  customisations: Record<string, string | string[] | ParsedQs | ParsedQs[]>;
  schemaKey: string;
};

/**
 * Displays a preview image of the current Avatar.
 *
 * Uses a combination of active form values and 'presets' from the schema.
 *
 * Must be within a `FormProvider` to access the form data.
 */
export default function AvatarPreview({
  customisations,
  schemaKey,
}: AvatarPreviewProps) {
  const router = useRouter();

  const findByKey = useFindByKey();

  // The name of this function is the same of a proprietaty testing library
  // function so we we need to disable the eslint rule
  // eslint-disable-next-line testing-library/await-async-query
  const field = findByKey(schemaKey);

  if (!field) {
    return null;
  }

  if (!("characterTypeIndex" in field) || !("characterIndex" in field)) {
    const error = Error(
      `Avatar role "${
        schemaKey.split(".")[1] || schemaKey
      }" does not exist in the schema`
    );

    Bugsnag.notify(error, (event) => {
      event.addMetadata("Query", router.query);
      event.severity = "error";
      event.unhandled = false;
    });

    throw error;
  }

  const combinedCustomisations: Record<
    string,
    string | string[] | ParsedQs | ParsedQs[]
  > = {};

  // Merge form values with `preset`s from schema
  Object.entries(field.keys).forEach(([key, entry]) => {
    if (customisations?.[key]) {
      combinedCustomisations[key] = customisations[key];
    } else if ("preset" in entry.metas[0] && entry.metas[0].preset) {
      combinedCustomisations[key] = entry.metas[0].preset;
    }

    // Some fields also affect others
    if ("presetOther" in entry.metas[0] && entry.metas[0].presetOther) {
      if (combinedCustomisations[key]) {
        const affectedPresets =
          entry.metas[0].presetOther[combinedCustomisations[key] as string];
        Object.entries(affectedPresets).forEach(([k, v]) => {
          // Only use this preset if the user hasn't manually selected something else
          if (!combinedCustomisations[k]) {
            combinedCustomisations[k] = v;
          }
        });
      }
    }
  });

  const src = field.metas[0].staticAssetPath
    ? field.metas[0].staticAssetPath
    : // ^ staticAssetPath doesn't take any customisations, so we are not appending any
      field.metas[0].assetPath +
      "&" +
      // ^ `assetPath` already has some params, so we're appending
      qs.stringify({
        count: field.characterTypeIndex,
        character: field.characterIndex,
        _width: 320,
        ...combinedCustomisations,
      });

  return (
    <span className={styles.preview}>
      <img
        alt={(combinedCustomisations.name as string) || ""}
        className={styles.avatar}
        src={src}
      />
      {field.metas[0].backgroundAssetPath && (
        <img
          alt=""
          aria-hidden
          className={styles.background}
          src={field.metas[0].backgroundAssetPath + "&_width=640"}
        />
      )}
    </span>
  );
}
